import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import GateScheduleEditor from './GateScheduleEditor';
import AutomationScheduleEditor from './AutomationScheduleEditor';

/**
 * "What would you like to configure?" menu for one or many gates.
 *
 * Two modes:
 * - Single-gate (`gateId` is set): saves through PUT /api/<role>/gates/:id/settings
 * - Bulk (`gateIds` is set): saves through PUT /api/<role>/gates/settings
 *
 * Each configurable feature has both an explicit on/off toggle and a
 * threshold value. Disabling silences the email and the visual cue on
 * the map for that feature on that gate. Adding a new alert type
 * (schedule violations, recipient routing, …) means appending one
 * entry to CONFIG_ITEMS — the rest of the chrome (toggle UI, save
 * button, bulk semantics, admin/customer URL prefix) is shared.
 */

const CONFIG_ITEMS = [
  {
    enabledKey: 'long_open_alert_enabled',
    valueKey: 'long_open_threshold_minutes',
    label: 'התראת שער פתוח זמן רב',
    description: 'אם השער פתוח יותר מהזמן שנקבע, יישלח מייל ובמפה יופיע צבע אזהרה. כבה אם זה לא רלוונטי לשער הזה (למשל שער שנפתח בבוקר ונסגר בערב).',
    valueLabel: 'סף התראה — דקות פתוח',
    valueDefault: 60,
    valueMin: 1,
    valuePlaceholder: '60',
  },
  {
    // Display-only toggle: shows/hides the live "minutes open" counter
    // drawn over the gate on the site map. No threshold, no email — it's
    // purely cosmetic, hence kind: 'toggle_only'. Off by default.
    kind: 'toggle_only',
    enabledKey: 'live_timer_enabled',
    label: 'טיימר זמן פתיחה על המפה',
    description: 'מציג מעל השער במפה מונה רץ של משך הזמן שהוא פתוח. כבוי כברירת מחדל — הפעל רק לשערים שבהם זה חשוב (למשל חדרי קירור). זה תצוגה בלבד; אינו משפיע על התראות.',
  },
  {
    enabledKey: 'fault_alert_enabled',
    label: 'התראת תקלה',
    description: 'משולש אזהרה אדום במפה ומייל אוטומטי כשנמצאת תקלה בשער. כבה אם אינך רוצה לקבל התראות תקלה על השער הזה.',
  },
  {
    enabledKey: 'offline_alert_enabled',
    valueKey: 'offline_threshold_minutes',
    label: 'התראת רכיב לא מחובר',
    description: 'אם הרכיב לא דיווח יותר מהזמן הזה, השער ידהה במפה ותישלח התראה. כבה אם זה לא קריטי עבור השער הזה.',
    valueLabel: 'סף — דקות',
    valueDefault: 20,
    valueMin: 1,
    valuePlaceholder: '20',
  },
  {
    // Schedule has a richer editor than the toggle+number form, so
    // we route it to GateScheduleEditor instead of the inline form.
    kind: 'schedule',
    enabledKey: 'unauthorized_open_alert_enabled',
    label: 'שעות פעילות + פתיחה לא מורשית',
    description: 'הגדר שעות פעילות לכל יום בשבוע. שער שייפתח מחוץ לשעות שהגדרת — תישלח התראה ויופיע סימון אדום על המפה.',
  },
  {
    // Auto-close — fires a close command at the configured trigger
    // times. Each automation now owns its own day-by-day schedule
    // (no longer keyed off "active hours"). See
    // AutomationScheduleEditor.
    kind: 'auto_close_schedule',
    enabledKey: 'auto_close_enabled',
    label: 'סגירה אוטומטית לפי שעה',
    description: 'הגדר לכל יום בשבוע שעה שבה השער ייסגר אוטומטית אם הוא פתוח. לא תלוי בשעות הפעילות — אפשר להגדיר רק לימים מסוימים. הפקודה תופיע בלוג הפקודות של השער.',
  },
  {
    // Auto-open — new feature. Fires an open command at the
    // configured trigger times. Skipped if the gate is already
    // open or hermetically locked at that moment.
    kind: 'auto_open_schedule',
    enabledKey: 'auto_open_enabled',
    label: 'פתיחה אוטומטית לפי שעה',
    description: 'הגדר לכל יום בשבוע שעה שבה השער ייפתח אוטומטית. שימושי לפתיחת שער בתחילת יום העבודה. אם השער נעול הרמטית באותו רגע, הפתיחה תידחה עד ששחרור הנעילה הושלם.',
  },
  {
    // Hermetic lock — physically immobilises the gate by opening
    // the O2 STOP-bridge for the configured windows. Independent
    // of "active hours": the operator can lock only sensitive
    // night hours (21:00 ← 06:00) without marking those hours as
    // off-shift. Only meaningful on toggle_with_stop wiring.
    kind: 'lock_schedule',
    enabledKey: 'hermetic_lock_enabled',
    label: 'נעילה הרמטית לפי חלון שעות',
    description: 'הגדר לכל יום בשבוע חלון זמן (משעה X עד שעה Y) שבו השער נעול הרמטית — לא ניתן להפעיל את המנוע. החלון יכול לחצות חצות (למשל 21:00 ← 06:00). דורש חיווט מסוג Toggle עם נעילה.',
  },
];

// Map config-item kind → automation-editor kind prop.
const AUTOMATION_KIND_BY_ITEM_KIND = {
  auto_close_schedule: 'auto_close',
  auto_open_schedule: 'auto_open',
  lock_schedule: 'lock',
};


function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        position: 'relative',
        width: '42px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        background: checked ? '#16a34a' : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s',
        padding: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: 'absolute',
        top: '2px',
        // RTL: start side flips, but the visual "knob" should sit on
        // the side that matches semantic on/off. We use left/right
        // computed numerically so it works regardless of dir.
        left: checked ? '20px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.15s',
      }} />
    </button>
  );
}


function GateConfigMenu({
  gateId = null,
  gateIds = null,
  groupId = null,
  initialSettings = null,
  apiBase = 'customer',
  onClose,
  onApplied,
}) {
  const isBulk = gateId == null && groupId == null && Array.isArray(gateIds) && gateIds.length > 0;
  const isGroup = groupId != null;
  const [stage, setStage] = useState('list');           // 'list' | 'edit'
  const [activeItem, setActiveItem] = useState(null);
  const [draftEnabled, setDraftEnabled] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEditing = (item) => {
    setActiveItem(item);
    const enabledFromCurrent = initialSettings ? !!initialSettings[item.enabledKey] : false;
    if (item.kind === 'schedule') {
      // The active-hours item swaps the menu out for the dedicated
      // weekly editor. Tracked through the same ``stage`` state
      // machine so there's still a single back-button + close path.
      setDraftEnabled(enabledFromCurrent);
      setStage('schedule');
      setError(null);
      return;
    }
    if (AUTOMATION_KIND_BY_ITEM_KIND[item.kind]) {
      // The three independent-automation editors share the same
      // shell (master toggle + day-by-day grid), parameterised by
      // ``kind``. They handle their own settings PUT internally,
      // so the surrounding GateConfigMenu just hands them control
      // and waits for ``onApplied``.
      setDraftEnabled(enabledFromCurrent);
      setStage('automation');
      setError(null);
      return;
    }
    // ``toggle_only`` items skip the threshold field entirely; the
    // default valueKey lookup below would just produce undefined which
    // is harmless, but staying explicit makes the behaviour easier to
    // follow.
    const valueFromCurrent =
      item.kind === 'toggle_only' || !item.valueKey
        ? null
        : (initialSettings ? initialSettings[item.valueKey] : null);
    setDraftEnabled(enabledFromCurrent);
    setDraftValue(valueFromCurrent == null ? '' : String(valueFromCurrent));
    setError(null);
    setStage('edit');
  };

  const backToList = () => {
    setStage('list');
    setActiveItem(null);
    setError(null);
  };

  const save = async () => {
    if (!activeItem) return;
    setSaving(true);
    setError(null);

    // Build the payload as a partial settings update.
    const payload = {
      [activeItem.enabledKey]: draftEnabled,
    };
    // Only include the threshold value if the customer typed one
    // and the item actually has a threshold (toggle_only items don't).
    if (activeItem.kind !== 'toggle_only' && activeItem.valueKey && draftValue !== '') {
      payload[activeItem.valueKey] = Number(draftValue);
    }

    try {
      if (isGroup) {
        await axios.put(`/api/${apiBase}/gate-groups/${groupId}/settings`, payload);
      } else if (isBulk) {
        await axios.put(`/api/${apiBase}/gates/settings`, {
          gate_ids: gateIds,
          settings: payload,
        });
      } else {
        await axios.put(`/api/${apiBase}/gates/${gateId}/settings`, payload);
      }
      onApplied && onApplied(payload);
      onClose && onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בשמירה');
      setSaving(false);
    }
  };

  const targetCount = isBulk ? gateIds.length : 1;
  const headerText = isGroup
    ? 'הגדרות הקבוצה'
    : isBulk
      ? `הגדרות עבור ${targetCount} שערים`
      : 'הגדרות שער';

  // Show "פעיל / כבוי" + current value next to each item in the list.
  const itemSummary = (item) => {
    if (!initialSettings) return null;
    const enabled = !!initialSettings[item.enabledKey];
    if (
      item.kind === 'schedule' ||
      item.kind === 'toggle_only' ||
      AUTOMATION_KIND_BY_ITEM_KIND[item.kind]
    ) {
      return enabled ? 'פעיל' : 'כבוי';
    }
    const value = initialSettings[item.valueKey];
    if (!enabled) return 'כבוי';
    if (value == null) return 'פעיל';
    return `פעיל · ${value} דק'`;
  };

  // Best-effort signal that a schedule probably exists. We don't
  // fetch the schedule itself here (would mean an extra round-trip
  // for a hint banner), so we fall back to the toggle the user would
  // have flipped while configuring it. False negatives are rare and
  // the hint is non-blocking either way.
  const scheduleLikelyDefined = !!(initialSettings && initialSettings.unauthorized_open_alert_enabled);

  // Schedule editor swaps the entire modal — render it instead of the
  // list/edit chrome. Closing it returns to the list.
  if (stage === 'schedule' && activeItem) {
    return (
      <GateScheduleEditor
        gateId={gateId}
        gateIds={gateIds}
        groupId={groupId}
        apiBase={apiBase}
        initialAlertEnabled={draftEnabled}
        onClose={() => { setStage('list'); setActiveItem(null); }}
        onApplied={() => { onApplied && onApplied(); }}
      />
    );
  }

  if (stage === 'automation' && activeItem) {
    const autoKind = AUTOMATION_KIND_BY_ITEM_KIND[activeItem.kind];
    return (
      <AutomationScheduleEditor
        kind={autoKind}
        gateId={gateId}
        groupId={groupId}
        apiBase={apiBase}
        initialEnabled={draftEnabled}
        onClose={() => { setStage('list'); setActiveItem(null); }}
        onApplied={() => { onApplied && onApplied(); }}
      />
    );
  }

  // Portal-mount the modal at document.body so the slide-panel's
  // `transform` (used to slide in/out) doesn't trap our `position:
  // fixed` overlay inside it — that was clipping the modal when this
  // component was rendered from the customer slide panel.
  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 110,
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: '#fff',
        borderRadius: '10px',
        padding: '1.25rem',
        width: 'min(420px, calc(100vw - 24px))',
        maxHeight: '90vh',
        overflowY: 'auto',
        zIndex: 111,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
          {stage === 'edit' && (
            <button
              onClick={backToList}
              aria-label="חזור"
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '1.1rem',
                cursor: 'pointer',
                padding: '0 0.25rem',
                color: '#6b7280',
              }}
            >
              ←
            </button>
          )}
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, flex: 1 }}>
            {stage === 'edit' && activeItem ? activeItem.label : headerText}
          </h3>
        </div>

        {stage === 'list' && (
          <>
            {isBulk && (
              <p style={{ margin: 0, marginBottom: '0.85rem', fontSize: '0.85rem', color: '#6b7280' }}>
                ההגדרה שתבחר תחול על כל {targetCount} השערים בו-זמנית.
              </p>
            )}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {CONFIG_ITEMS.map(item => {
                const summary = itemSummary(item);
                return (
                  <li key={item.enabledKey}>
                    <button
                      onClick={() => startEditing(item)}
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem 0.85rem',
                        border: '1px solid #e5e7eb',
                        background: '#fff',
                        borderRadius: '6px',
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        textAlign: 'right',
                        fontSize: '0.9rem',
                      }}
                    >
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span>{item.label}</span>
                        {summary && (
                          <span style={{
                            fontSize: '0.72rem',
                            color: summary === 'כבוי' ? '#9ca3af' : '#16a34a',
                            marginTop: '0.15rem',
                          }}>
                            {summary}
                          </span>
                        )}
                      </span>
                      <span style={{ color: '#9ca3af' }}>‹</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '0.45rem 0.9rem',
                  border: 'none',
                  background: 'transparent',
                  color: '#6b7280',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                סגור
              </button>
            </div>
          </>
        )}

        {stage === 'edit' && activeItem && (
          <>
            {activeItem.description && (
              <p style={{ margin: 0, marginBottom: '1rem', fontSize: '0.8rem', color: '#6b7280' }}>
                {activeItem.description}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.7rem 0.85rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              marginBottom: '0.85rem',
              background: '#f9fafb',
            }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                {activeItem.kind === 'toggle_only'
                  ? (draftEnabled ? 'הפיצ\'ר פעיל' : 'הפיצ\'ר כבוי')
                  : (draftEnabled ? 'התראה פעילה' : 'התראה כבויה')}
              </span>
              <ToggleSwitch
                checked={draftEnabled}
                onChange={setDraftEnabled}
              />
            </div>

            {activeItem.kind === 'toggle_only' && activeItem.warnIfNoActiveHours && draftEnabled && !scheduleLikelyDefined && !isBulk && (
              <div style={{
                padding: '0.6rem 0.75rem',
                background: '#fffbeb',
                color: '#92400e',
                border: '1px solid #fde68a',
                borderRadius: '5px',
                fontSize: '0.78rem',
                marginBottom: '0.85rem',
              }}>
                שים לב: לא הוגדרו שעות פעילות לשער הזה. הסגירה האוטומטית תופעל אבל לא יהיה לה במה לפעול. הגדר שעות פעילות בפריט "שעות פעילות + פתיחה לא מורשית".
              </div>
            )}

            {activeItem.kind !== 'toggle_only' && activeItem.valueLabel && (
              <>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  marginBottom: '0.35rem',
                  opacity: draftEnabled ? 1 : 0.4,
                }}>
                  {activeItem.valueLabel}
                </label>
                <input
                  type="number"
                  min={activeItem.valueMin}
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  placeholder={activeItem.valuePlaceholder}
                  disabled={!draftEnabled}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '0.5rem 0.7rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '5px',
                    fontSize: '0.95rem',
                    background: draftEnabled ? '#fff' : '#f3f4f6',
                    color: draftEnabled ? '#111827' : '#9ca3af',
                  }}
                />
              </>
            )}

            {error && (
              <div style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.6rem' }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button
                onClick={backToList}
                disabled={saving}
                style={{
                  padding: '0.45rem 0.9rem',
                  border: 'none',
                  background: 'transparent',
                  color: '#6b7280',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                onClick={save}
                disabled={saving}
                style={{
                  padding: '0.45rem 1.1rem',
                  border: 'none',
                  background: '#1f2937',
                  color: '#fff',
                  borderRadius: '5px',
                  fontSize: '0.85rem',
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'שומר...' : (isBulk ? 'החל על כולם' : 'שמור')}
              </button>
            </div>
          </>
        )}
      </div>
    </>,
    document.body
  );
}

export default GateConfigMenu;
